import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, ShieldAlert, AlertOctagon, RefreshCw,
  CheckCircle2, Eye, ArrowLeft, FileText, User, Clock, Filter,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_BADGE = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warning:  'bg-amber-50 text-amber-700 border-amber-200',
  info:     'bg-slate-50 text-slate-700 border-slate-200',
};
const SEV_DOT = { critical: 'bg-red-600', warning: 'bg-amber-500', info: 'bg-slate-400' };

const STATUS_FILTERS = [
  { value: 'open',         label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'all',          label: 'All' },
];
const SEV_FILTERS = [
  { value: '',         label: 'All severity' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning',  label: 'Warning' },
  { value: 'info',     label: 'Info' },
];

const fmtTime = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const Tile = ({ testid, label, value, icon: Icon, tone }) => (
  <div data-testid={testid} className="cn-card p-5">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
        <p className="cn-display text-3xl text-cn-text mt-1.5 tabular-nums">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

/**
 * Standalone "Documentation Safety Shield" page — the queue surfaced by the
 * in-app notification deep-link (`/admin/safety-shield`). Shows every NLP
 * scan flagged for supervisor review with one-click acknowledge.
 *
 * Backend contracts:
 *   GET  /api/admin/safety-shield?status=open|acknowledged|all&severity=...
 *   POST /api/admin/safety-shield/{scan_id}/acknowledge
 */
export default function AdminSafetyShield() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ status });
      if (severity) params.set('severity', severity);
      const { data } = await axios.get(`${API}/admin/safety-shield?${params.toString()}`, authHeader);
      setScans(data.scans || []);
      setOpenCount(data.open_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the safety queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [status, severity, token]);

  useEffect(() => { if (token) load(); }, [load, token]);

  // Soft auto-refresh: re-pulls every 60s so a supervisor sitting on the queue
  // sees new alerts the moment Cara escalates them.
  useEffect(() => {
    if (!token) return undefined;
    const id = setInterval(() => load(true), 60_000);
    return () => clearInterval(id);
  }, [load, token]);

  const acknowledge = async (scanId) => {
    setBusyId(scanId);
    try {
      await axios.post(`${API}/admin/safety-shield/${scanId}/acknowledge`, {}, authHeader);
      toast.success('Acknowledged');
      // Optimistic: if we're in the open view, remove the row; otherwise mark it.
      setScans((prev) => prev.map((s) => s.id === scanId
        ? { ...s, acknowledged_by: user?.id, acknowledged_at: new Date().toISOString() }
        : s));
      setOpenCount((n) => Math.max(0, n - 1));
      if (status === 'open') {
        setScans((prev) => prev.filter((s) => s.id !== scanId));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not acknowledge');
    } finally {
      setBusyId(null);
    }
  };

  // Severity-tone tiles for the at-a-glance summary across the current filter.
  const counts = scans.reduce((acc, s) => {
    acc.total += 1;
    acc[s.severity] = (acc[s.severity] || 0) + 1;
    return acc;
  }, { total: 0, critical: 0, warning: 0, info: 0 });

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="admin-safety-shield-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1 text-xs font-semibold text-cn-text-muted hover:text-cn-primary mb-3"
              data-testid="safety-shield-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Admin dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-cn-primary text-white flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h1 className="cn-display text-3xl text-cn-text">Safety Shield</h1>
                <p className="text-sm text-cn-text-muted">
                  Documentation alignment scans — AI flags notes where the
                  visit documentation doesn&apos;t match the patient&apos;s risk profile.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="cn-btn cn-btn-ghost !py-2 text-xs"
            data-testid="safety-shield-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {' '}Refresh
          </button>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Tile testid="safety-tile-open"     label="Open"     value={openCount}      icon={AlertOctagon} tone="bg-red-100 text-red-600" />
          <Tile testid="safety-tile-critical" label="Critical" value={counts.critical} icon={ShieldAlert}  tone="bg-red-100 text-red-600" />
          <Tile testid="safety-tile-warning"  label="Warning"  value={counts.warning}  icon={ShieldAlert}  tone="bg-amber-100 text-amber-600" />
          <Tile testid="safety-tile-info"     label="Info"     value={counts.info}     icon={ShieldCheck}  tone="bg-slate-100 text-slate-600" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="safety-shield-filters">
          <Filter className="w-4 h-4 text-cn-text-muted" />
          <div className="inline-flex rounded-xl border border-cn-border bg-white p-0.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatus(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  status === f.value ? 'bg-cn-primary text-white' : 'text-cn-text-muted hover:text-cn-text'
                }`}
                data-testid={`safety-filter-status-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="cn-input !py-1.5 !px-3 text-xs"
            data-testid="safety-filter-severity"
          >
            {SEV_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        {/* Queue */}
        {loading ? (
          <div className="cn-card p-12 flex items-center justify-center" data-testid="safety-shield-loading">
            <Loader2 className="w-6 h-6 animate-spin text-cn-primary" />
          </div>
        ) : scans.length === 0 ? (
          <div className="cn-card p-12 text-center" data-testid="safety-shield-empty">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <h3 className="font-display font-bold text-lg text-cn-text">No alerts in this view</h3>
            <p className="text-sm text-cn-text-muted mt-1">
              {status === 'open'
                ? 'All clear — every locked note this week aligned with the patient risk profile.'
                : 'Nothing matches the current filters.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="safety-shield-list">
            {scans.map((s) => {
              const isExpanded = expanded === s.id;
              const isAcked = !!s.acknowledged_by;
              return (
                <li key={s.id} className="cn-card p-5" data-testid={`safety-scan-row-${s.id}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0 ${SEV_DOT[s.severity] || SEV_DOT.info}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${SEV_BADGE[s.severity] || SEV_BADGE.info}`}>
                          {s.severity}
                        </span>
                        <span className="text-[11px] text-cn-text-muted">
                          fall-risk: <strong>{s.fall_risk_at_scan || 'unknown'}</strong>
                        </span>
                        {s.llm_unavailable && (
                          <span className="text-[10px] font-semibold text-cn-text-muted px-1.5 py-0.5 rounded border border-cn-border">
                            heuristic
                          </span>
                        )}
                        {isAcked && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                            <CheckCircle2 className="w-3 h-3" /> acknowledged {fmtTime(s.acknowledged_at)}
                          </span>
                        )}
                      </div>
                      <p className="font-display font-bold text-base text-cn-text mt-2 leading-tight" data-testid={`safety-scan-summary-${s.id}`}>
                        {s.summary || 'Documentation alignment alert'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-[11px] text-cn-text-muted">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(s.created_at)}</span>
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />clinician {String(s.clinician_id || '').slice(0, 8)}</span>
                        <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />note {String(s.note_id || '').slice(0, 8)}</span>
                      </div>
                      {isExpanded && (
                        <div className="mt-4 space-y-3 text-sm">
                          {s.risk_flags?.length > 0 && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Risk flags</p>
                              <ul className="space-y-1">
                                {s.risk_flags.map((f, i) => (
                                  <li key={i} className="text-cn-text">• {f}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {s.missing_documentation?.length > 0 && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Missing documentation</p>
                              <ul className="space-y-1">
                                {s.missing_documentation.map((m, i) => (
                                  <li key={i} className="text-cn-text">• {m}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="flex items-center gap-3 pt-2">
                            <Link
                              to={`/clinician/notes/${s.note_id}`}
                              className="text-xs font-semibold text-cn-primary hover:underline inline-flex items-center gap-1"
                              data-testid={`safety-scan-open-note-${s.id}`}
                            >
                              <Eye className="w-3 h-3" /> Open the note
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <button
                        onClick={() => setExpanded(isExpanded ? null : s.id)}
                        className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs"
                        data-testid={`safety-scan-toggle-${s.id}`}
                      >
                        {isExpanded ? 'Hide' : 'Details'}
                      </button>
                      {!isAcked && (
                        <button
                          onClick={() => acknowledge(s.id)}
                          disabled={busyId === s.id}
                          className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs"
                          data-testid={`safety-scan-ack-${s.id}`}
                        >
                          {busyId === s.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <CheckCircle2 className="w-3 h-3" />}
                          {' '}Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
